/**
 * OutputFormatsDiffView — friendly chip view for a `handler_config`
 * proposal that changes `output_formats` (the artifacts / "Output
 * formats" tab: pptx, docx, gforms, markdown).
 *
 * Mirrors AllowedModelsDiffView: the AoA fires
 * `propose_edit_agent(field='handler_config', new_value='{
 *   "output_formats": {"docx": true, "markdown": true} }')`
 * and instead of raw JSON the user sees which document types the agent
 * will be able to produce after the change — turned-on formats as added
 * chips, turned-off ones under a removed heading.
 */

import React, { useMemo } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import './AllowedModelsDiffView.css';

/** Selectable formats (backend SELECTABLE_OUTPUT_FORMATS — keep in sync).
 *  Labels are product nouns; the descriptive sub-lines come from i18n. */
const FORMAT_LABELS: Record<string, string> = {
  pptx: 'PowerPoint',
  docx: 'Word',
  gforms: 'Google Forms',
  markdown: 'Markdown',
};

interface Props {
  /** Current output_formats map (from proposal.old_value.output_formats). */
  before: Record<string, boolean> | null | undefined;
  /** Proposed output_formats map (from proposal.new_value.output_formats). */
  after: Record<string, boolean> | null | undefined;
}

const enabledOf = (v: Record<string, boolean> | null | undefined): string[] =>
  Object.entries(v || {}).filter(([, on]) => !!on).map(([k]) => k);

export const OutputFormatsDiffView: React.FC<Props> = ({ before, after }) => {
  const { t } = useLanguage();
  const { kept, added, removed, afterEnabled } = useMemo(() => {
    const beforeSet = new Set(enabledOf(before));
    const afterEnabled = enabledOf(after);
    // Formats absent from `after` keep their previous state (the backend
    // shallow-merges output_formats keys it receives) — treat them as kept.
    const inherited = [...beforeSet].filter((f) => !(after && f in after));
    const afterSet = new Set([...afterEnabled, ...inherited]);
    return {
      kept: [...afterSet].filter((f) => beforeSet.has(f)),
      added: afterEnabled.filter((f) => !beforeSet.has(f)),
      removed: [...beforeSet].filter((f) => !afterSet.has(f)),
      afterEnabled: [...afterSet],
    };
  }, [before, after]);

  const renderChip = (fmt: string, variant: 'kept' | 'added' | 'removed') => (
    <span
      key={`${variant}-${fmt}`}
      className={`aoa-allowed-chip aoa-allowed-chip--${variant}`}
      title={fmt}
    >
      <span className="aoa-allowed-chip__label">{FORMAT_LABELS[fmt] || fmt}</span>
      <span className="aoa-allowed-chip__provider">
        {t(`metaAgent.proposal.outputFormats.${fmt}Sub`)}
      </span>
    </span>
  );

  return (
    <div className="aoa-allowed-models" data-testid="aoa-output-formats-diff">
      <div className="aoa-allowed-models__section">
        <div className="aoa-allowed-models__heading">
          {t('metaAgent.proposal.outputFormats.heading')}
        </div>
        {afterEnabled.length === 0 ? (
          <div className="aoa-allowed-models__chips">
            <span className="aoa-allowed-chip aoa-allowed-chip--all">
              <span className="aoa-allowed-chip__label">
                {t('metaAgent.proposal.outputFormats.noneLabel')}
              </span>
              <span className="aoa-allowed-chip__provider">
                {t('metaAgent.proposal.outputFormats.noneSub')}
              </span>
            </span>
          </div>
        ) : (
          <div className="aoa-allowed-models__chips">
            {kept.map((f) => renderChip(f, 'kept'))}
            {added.map((f) => renderChip(f, 'added'))}
          </div>
        )}
      </div>

      {removed.length > 0 && (
        <div className="aoa-allowed-models__section">
          <div className="aoa-allowed-models__heading aoa-allowed-models__heading--removed">
            {t('metaAgent.proposal.outputFormats.removedHeading')}
          </div>
          <div className="aoa-allowed-models__chips">
            {removed.map((f) => renderChip(f, 'removed'))}
          </div>
        </div>
      )}
    </div>
  );
};
